export const englishCompositionCourse = {
  id: "english-composition-2024",
  title: "English Composition: Writing for Academic and Professional Success",
  shortDescription: "Master essential writing skills including argumentation, research, citation, and rhetorical strategies for academic and professional contexts.",
  description: "Comprehensive course developing critical writing abilities through rhetorical analysis, research-based writing, argumentative essays, and professional communication formats.",
  category: "Communication",
  industryCategory: "Communications & Media",
  difficulty: "beginner",
  duration: 15,
  estimatedWeeks: 15,
  instructorName: "Workazon Platform",
  instructorId: "platform",
  instructorBio: "Expert-curated content from writing instructors and professional communicators",
  thumbnailUrl: "https://images.unsplash.com/photo-1455390582262-044cdead277a?w=400",
  price: 0,
  isPublic: true,
  status: "published",
  template: "comprehensive",
  version: "1.0",
  createdBy: "platform",
  createdAt: new Date(),
  lastModified: new Date(),
  updatedAt: new Date(),

  learningObjectives: [
    "Develop clear, coherent, and persuasive written arguments",
    "Master research skills and proper citation methods",
    "Understand and apply rhetorical strategies for different audiences",
    "Write effectively in multiple genres and formats", 
    "Develop revision and editing skills for polished prose",
    "Analyze and critique written texts effectively",
    "Create professional documents for workplace communication",
    "Build confidence and fluency in academic writing"
  ],

  prerequisites: [
    "High School Diploma or Equivalent",
    "Basic Reading Comprehension",
    "Fundamental Grammar Knowledge",
    "Access to Word Processing Software"
  ],

  skills: [
    "Academic Writing",
    "Research and Documentation",
    "Rhetorical Analysis", 
    "Critical Thinking",
    "Editing and Revision",
    "Professional Writing",
    "Source Evaluation",
    "Argumentation"
  ],

  tags: [
    "Writing",
    "English Composition", 
    "Academic Writing",
    "Research Skills",
    "Rhetoric",
    "Professional Communication",
    "Critical Thinking",
    "Documentation"
  ],

  marketData: {
    averageSalary: 58000,
    salaryRange: {
      min: 40000,
      max: 85000
    },
    jobDemand: 4.1,
    marketTrend: "stable",
    careerPaths: [
      "Technical Writer",
      "Content Specialist",
      "Communications Coordinator",
      "Grant Writer",
      "Copy Editor",
      "Marketing Writer"
    ],
    relatedSkills: [
      "Content Marketing",
      "Copywriting",
      "Editing",
      "Research",
      "Communication Strategy",
      "Digital Publishing"
    ]
  },

  enrollmentCount: 0,
  completionRate: 0,
  averageRating: 0,
  reviewCount: 0,

  modules: [
    {
      id: "writing-fundamentals",
      title: "Writing Fundamentals and the Writing Process",
      description: "Essential writing principles, process, and rhetorical foundations",
      order: 1,
      estimatedHours: 5.5,
      content: [
        {
          id: "rhetorical-situation",
          title: "Understanding the Rhetorical Situation",
          description: "Analyzing audience, purpose, context, and constraints in writing",
          type: "video",
          duration: 40
        },
        {
          id: "writing-process-workshop",
          title: "Writing Process Workshop",
          description: "Practice prewriting, drafting, revising, and editing strategies",
          type: "communication-interactive",
          duration: 50,
          interactiveData: {
            exerciseType: "written-communication",
            instructions: "Work through the complete writing process for a persuasive essay. Demonstrate understanding of audience analysis, purpose clarification, prewriting strategies, drafting techniques, and revision approaches.",
            scenario: {
              writingContext: "You are writing a persuasive essay for your college newspaper arguing for a specific change to campus policy (extended library hours, improved campus food options, enhanced mental health services, etc.). Your audience includes fellow students, faculty, and administrators."
            },
            hints: [
              "Start with thorough audience analysis and purpose clarification",
              "Use prewriting techniques like brainstorming, outlining, or freewriting",
              "Focus on clear thesis development and supporting arguments",
              "Plan your revision strategy for multiple drafts"
            ]
          }
        },
        {
          id: "rhetorical-appeals",
          title: "Rhetorical Appeals and Persuasive Strategies",
          description: "Applying ethos, pathos, and logos in academic and professional writing",
          type: "text",
          duration: 45,
          textContent: `# Rhetorical Appeals and Persuasive Strategies

## The Rhetorical Triangle

### Understanding Rhetoric in Writing

**Rhetorical Definition:**
- **Aristotelian Foundation**: The art of persuasion through effective communication
- **Modern Application**: Strategic communication designed to influence audiences
- **Writing Context**: Using language strategically to achieve specific purposes
- **Audience-Centered**: Adapting communication to specific readers and contexts
- **Purposeful Communication**: Every writing choice serves rhetorical goals

**Rhetorical Situation Components:**
- **Writer (Rhetor)**: The person creating the communication
- **Audience**: The intended readers or recipients of the message
- **Purpose**: The specific goal or intended outcome of the communication
- **Context**: The circumstances surrounding the communication event
- **Genre**: The type or format of communication being used

### Ethos: Credibility and Character

**Building Writer Credibility:**

**Expertise Demonstration:**
- **Knowledge Display**: Showing deep understanding of the topic through specific details
- **Research Citation**: Using credible sources to support arguments and claims
- **Professional Experience**: Drawing on relevant personal or professional background
- **Balanced Perspective**: Acknowledging complexity and multiple viewpoints
- **Precision in Language**: Using accurate terminology and avoiding overgeneralization

**Character and Trustworthiness:**
- **Honesty**: Presenting information accurately without distortion
- **Fairness**: Representing opposing viewpoints accurately before refuting them
- **Humility**: Acknowledging limitations and areas of uncertainty
- **Respect**: Treating readers and opponents with dignity and consideration
- **Transparency**: Being clear about biases, assumptions, and potential conflicts of interest

**Professional Presentation:**
- **Format Adherence**: Following appropriate conventions for the genre and context
- **Grammar and Mechanics**: Demonstrating careful attention to language correctness
- **Organization**: Presenting ideas in logical, clear, and accessible structure
- **Citation Standards**: Properly crediting sources according to academic standards
- **Tone Appropriateness**: Matching tone to audience expectations and context

### Pathos: Emotional Appeals

**Appropriate Emotional Engagement:**

**Narrative Techniques:**
- **Personal Stories**: Using anecdotes that create emotional connection with readers
- **Case Studies**: Presenting specific examples that illustrate broader points
- **Vivid Description**: Creating sensory details that help readers experience situations
- **Character Development**: Presenting people as complex individuals rather than abstractions
- **Dramatic Structure**: Building tension and resolution to maintain reader engagement

**Language Choices:**
- **Connotative Language**: Choosing words with emotional associations beyond literal meaning
- **Metaphor and Analogy**: Using comparisons that create emotional understanding
- **Rhythm and Pacing**: Varying sentence structure to create emotional flow
- **Active Voice**: Creating energy and immediacy through verb choices
- **Concrete Details**: Using specific, tangible examples rather than abstract concepts

**Ethical Emotional Appeals:**
- **Legitimate Concerns**: Appealing to emotions that are relevant to the argument
- **Proportional Response**: Matching emotional intensity to the significance of the issue
- **Respect for Audience**: Avoiding manipulation or exploitation of fears and prejudices
- **Supportive Evidence**: Combining emotional appeals with logical support
- **Constructive Purpose**: Using emotion to motivate positive action rather than mere reaction

### Logos: Logical Appeals and Evidence

**Logical Structure:**

**Argument Organization:**
- **Clear Thesis**: Presenting a specific, arguable claim that guides the entire piece
- **Logical Progression**: Arranging points in order that builds understanding and persuasion
- **Paragraph Unity**: Ensuring each paragraph focuses on a single main idea
- **Transitions**: Creating clear connections between ideas and sections
- **Conclusion Integration**: Synthesizing arguments rather than merely summarizing

**Evidence Types:**
- **Statistical Data**: Using numerical information to support quantitative claims
- **Expert Testimony**: Quoting authorities who have relevant expertise and credibility
- **Research Studies**: Citing peer-reviewed research that supports arguments
- **Historical Examples**: Drawing on past events that illustrate current points
- **Analogical Evidence**: Using comparisons to similar situations or contexts

**Reasoning Patterns:**

**Deductive Reasoning:**
- **Major Premise**: General principle or widely accepted truth
- **Minor Premise**: Specific case or application of the general principle
- **Conclusion**: Logical result that follows from the premises
- **Syllogistic Structure**: Formal logical progression from general to specific
- **Validity Testing**: Ensuring conclusions follow logically from premises

**Inductive Reasoning:**
- **Specific Observations**: Concrete examples, data points, or case studies
- **Pattern Recognition**: Identifying commonalities across multiple examples
- **Generalization**: Drawing broader conclusions from specific evidence
- **Probability Assessment**: Acknowledging degrees of certainty in conclusions
- **Representative Sampling**: Ensuring examples are typical rather than exceptional

**Causal Reasoning:**
- **Cause Identification**: Establishing what factors lead to specific outcomes
- **Effect Analysis**: Examining consequences of particular actions or conditions
- **Correlation vs. Causation**: Distinguishing between relationships and causal connections
- **Multiple Causation**: Recognizing that most effects have multiple contributing causes
- **Temporal Sequence**: Establishing chronological relationships between causes and effects

## Integrating Rhetorical Appeals

### Balanced Persuasion

**Strategic Combination:**
- **Audience Analysis**: Determining which appeals will be most effective with specific readers
- **Context Consideration**: Adapting appeal emphasis to situational requirements
- **Purpose Alignment**: Choosing appeals that support specific writing goals
- **Genre Conventions**: Following expectations for different types of writing
- **Cultural Sensitivity**: Considering how different audiences respond to various appeals

**Appeal Proportions:**
- **Academic Writing**: Heavy emphasis on logos with supporting ethos and minimal pathos
- **Public Policy Arguments**: Balanced use of all three appeals for broad audience persuasion
- **Personal Essays**: Strong pathos with credible ethos and logical organization
- **Professional Reports**: Primary logos and ethos with strategic emotional appeals
- **Opinion Pieces**: Dynamic integration of all appeals for maximum persuasive impact

### Avoiding Logical Fallacies

**Common Fallacies to Avoid:**

**Ad Hominem:**
- **Personal Attacks**: Attacking the person rather than addressing their arguments
- **Character Assassination**: Using irrelevant personal information to discredit opponents
- **Genetic Fallacy**: Rejecting ideas based on their source rather than their merit
- **Proper Alternative**: Address arguments directly while maintaining respect for persons

**False Dichotomy:**
- **Oversimplification**: Presenting only two options when more alternatives exist
- **Black-and-White Thinking**: Ignoring middle ground or nuanced positions
- **Forced Choice**: Creating artificial either/or scenarios
- **Proper Alternative**: Acknowledge the full range of available options and positions

**Hasty Generalization:**
- **Insufficient Evidence**: Drawing broad conclusions from limited examples
- **Stereotyping**: Applying characteristics to entire groups based on few instances
- **Overgeneralization**: Making universal claims based on specific cases
- **Proper Alternative**: Ensure evidence is representative and sufficient for conclusions

**Slippery Slope:**
- **Unwarranted Progression**: Claiming one event will inevitably lead to extreme consequences
- **Causal Exaggeration**: Overestimating the likelihood of negative outcomes
- **Fear Mongering**: Using unrealistic scenarios to motivate opposition
- **Proper Alternative**: Evaluate each step in a causal chain based on actual probability

**Appeal to Authority:**
- **Irrelevant Expertise**: Citing authorities outside their area of competence
- **False Authority**: Using celebrity or popularity rather than genuine expertise
- **Outdated Authority**: Relying on information that is no longer current or valid
- **Proper Alternative**: Use authorities with relevant, current expertise and explain their qualifications

## Practical Application Strategies

### Audience-Centered Writing

**Audience Analysis:**
- **Demographics**: Age, education, profession, cultural background of readers
- **Prior Knowledge**: What readers already know and what needs explanation
- **Attitudes**: Existing beliefs, values, and positions on the topic
- **Expectations**: What readers want from the communication
- **Constraints**: Time, attention, and processing limitations of audience

**Adaptation Strategies:**
- **Vocabulary Level**: Choosing appropriate terminology for audience expertise
- **Evidence Selection**: Using examples and sources that resonate with specific readers
- **Organizational Pattern**: Structuring arguments in ways that match audience preferences
- **Tone Adjustment**: Adopting formal or informal voice as appropriate for context
- **Cultural Sensitivity**: Considering diverse perspectives and avoiding assumptions

### Purpose-Driven Writing

**Clear Purpose Definition:**
- **Primary Goal**: The main outcome you want to achieve through writing
- **Secondary Goals**: Additional benefits or effects you hope to accomplish
- **Specific Actions**: Concrete behaviors you want readers to perform
- **Attitude Changes**: Shifts in thinking or feeling you want to promote
- **Knowledge Transfer**: Information you want readers to understand and retain

**Purpose-Driven Strategies:**
- **Thesis Crafting**: Creating specific, arguable claims that support your purpose
- **Evidence Selection**: Choosing support that directly advances your goals
- **Organizational Choice**: Structuring content to maximize purposeful impact
- **Appeal Integration**: Using ethos, pathos, and logos to achieve specific outcomes
- **Call to Action**: Including clear directions for reader response when appropriate

Understanding and skillfully applying rhetorical appeals enables writers to create persuasive, effective communication that achieves its intended purposes while maintaining ethical standards and respect for audiences.`
        }
      ],
      quiz: {
        id: "writing-fundamentals-quiz",
        title: "Writing Fundamentals Quiz",
        description: "Test your understanding of rhetorical principles and writing process",
        timeLimit: 20,
        passingScore: 80,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "Which rhetorical appeal primarily focuses on the credibility of the writer?",
            options: [
              "Pathos",
              "Logos", 
              "Ethos",
              "Kairos"
            ],
            correctAnswer: 2,
            explanation: "Ethos refers to the credibility, character, and trustworthiness of the writer, which influences the audience's willingness to accept the message and arguments presented."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "What is the most important factor to consider when analyzing your rhetorical situation?",
            options: [
              "The length of your essay",
              "Your audience's needs and expectations",
              "The number of sources required",
              "The due date for submission"
            ],
            correctAnswer: 1,
            explanation: "Understanding your audience's needs, expectations, knowledge level, and attitudes is crucial for making effective rhetorical choices in your writing."
          },
          {
            id: "q3",
            type: "multiple-choice",
            question: "Which of the following is an example of a logical fallacy?",
            options: [
              "Using expert testimony to support a claim",
              "Providing statistical evidence for an argument",
              "Attacking a person's character instead of their argument", 
              "Using emotional appeals in persuasive writing"
            ],
            correctAnswer: 2,
            explanation: "Attacking a person's character instead of addressing their argument is called an ad hominem fallacy, which is a logical error in reasoning."
          }
        ]
      }
    },
    {
      id: "research-documentation",
      title: "Research Skills and Documentation",
      description: "Finding, evaluating, and citing sources using academic standards",
      order: 2,
      estimatedHours: 6.0,
      content: [
        {
          id: "source-evaluation",
          title: "Evaluating Sources and Information Literacy",
          description: "Critical analysis of source credibility and relevance",
          type: "video",
          duration: 45
        },
        {
          id: "research-project",
          title: "Research and Citation Workshop",
          description: "Conduct research and practice proper citation in MLA, APA, and Chicago styles",
          type: "communication-interactive",
          duration: 60,
          interactiveData: {
            exerciseType: "written-communication",
            instructions: "Conduct research on a contemporary issue and create a properly documented research proposal. Demonstrate source evaluation skills, proper citation methods, and integration of sources into your own writing.",
            scenario: {
              writingContext: "You are preparing a research proposal for a 10-page argumentative research paper on a current social, environmental, or technological issue. Your proposal must include an annotated bibliography with at least 8 credible sources from diverse types (scholarly articles, books, reputable websites, primary sources) with proper citations."
            },
            hints: [
              "Use the CRAAP test (Currency, Relevance, Authority, Accuracy, Purpose) to evaluate sources",
              "Include a mix of primary and secondary sources",
              "Practice proper in-text citations and bibliography formatting",
              "Write annotations that summarize, evaluate, and explain source relevance"
            ]
          }
        },
        {
          id: "citation-integration",
          title: "Source Integration and Citation Styles",
          description: "Mastering MLA, APA, and Chicago citation formats",
          type: "text",
          duration: 50,
          textContent: `# Source Integration and Citation Styles

## Information Literacy Fundamentals

### Source Evaluation Criteria

**CRAAP Test for Source Evaluation:**

**Currency:**
- **Publication Date**: When was the information published or last updated?
- **Timeliness**: Is the information current enough for your topic and purpose?
- **Link Functionality**: Do links work and lead to current, relevant information?
- **Revision History**: Has the source been updated to reflect new developments?
- **Temporal Relevance**: Does the time period of publication affect the information's value?

**Relevance:**
- **Topic Alignment**: Does the information directly relate to your research question?
- **Audience Appropriateness**: Is the source written for an appropriate audience level?
- **Geographic Scope**: Does the source cover the geographic area relevant to your research?
- **Scope and Depth**: Does the source provide sufficient detail for your needs?
- **Unique Contribution**: Does this source add new information or perspective?

**Authority:**
- **Author Credentials**: What are the author's qualifications, education, and experience?
- **Institutional Affiliation**: Is the author associated with a reputable organization?
- **Publisher Reputation**: Is the publisher known for quality and reliability?
- **Peer Review**: Has the source undergone academic peer review?
- **Citation by Others**: Do other reputable sources cite this work?

**Accuracy:**
- **Evidence Quality**: Are claims supported by reliable evidence and documentation?
- **Fact-Checking**: Can factual claims be verified through other sources?
- **Methodology**: Are research methods clearly described and appropriate?
- **Error Identification**: Are there obvious errors in grammar, spelling, or logic?
- **Bias Recognition**: Is information presented objectively or with clear bias?

**Purpose:**
- **Intended Audience**: Who is the target audience for this information?
- **Author Motivation**: Why did the author create this source?
- **Commercial Interests**: Is the source trying to sell something or promote a particular viewpoint?
- **Educational Value**: Does the source aim to inform, persuade, entertain, or sell?
- **Objectivity**: Is the tone balanced and professional?

### Types of Sources

**Primary Sources:**
- **Original Documents**: Letters, diaries, speeches, interviews, government documents
- **Research Data**: Original research studies, surveys, experiments, statistical data
- **Creative Works**: Literature, art, music, films created during the period being studied
- **Eyewitness Accounts**: First-hand testimonies, memoirs, autobiographies
- **Contemporary Records**: Newspapers, magazines, official records from the time period

**Secondary Sources:**
- **Scholarly Articles**: Peer-reviewed research published in academic journals
- **Books by Experts**: Monographs and edited volumes by subject matter experts
- **Review Articles**: Comprehensive surveys of research on specific topics
- **Documentaries**: Well-researched films that analyze historical or contemporary issues
- **Government Reports**: Official analyses and recommendations from government agencies

**Tertiary Sources:**
- **Encyclopedias**: General and specialized reference works
- **Textbooks**: Educational materials that synthesize information from multiple sources
- **Handbooks**: Comprehensive guides to specific fields or topics
- **Bibliographies**: Lists of sources on particular topics
- **Indexes and Databases**: Tools for finding primary and secondary sources

## Citation Style Basics

### MLA Style (Modern Language Association)

**When to Use MLA:**
- **Humanities Disciplines**: Literature, languages, cultural studies, philosophy
- **English Composition**: Academic writing courses and assignments
- **Arts and Media**: Film studies, theater, music, visual arts
- **Comparative Studies**: Interdisciplinary humanities research
- **Creative Writing**: Academic analysis of creative works

**MLA In-Text Citations:**

**Author-Page Format:**
- **Basic Citation**: (Author Page#) - (Smith 45)
- **Multiple Authors**: (Smith and Jones 67)
- **No Author**: (\"Title\" 23) or (\"Shortened Title\" 23)
- **Corporate Author**: (American Red Cross 12)
- **Multiple Works**: (Smith 45; Jones 67)

**Signal Phrases:**
- **Author Introduction**: Smith argues that \"quotation\" (45).
- **Variation**: According to Smith, \"quotation\" (45).
- **Paraphrase**: Smith suggests that the evidence supports this conclusion (45).
- **Multiple Sources**: As several scholars note (Smith 45; Jones 67; Brown 89)...

**Works Cited Formatting:**
- **Book**: Author. Title. Publisher, Year.
- **Journal Article**: Author. \"Title.\" Journal, vol. #, no. #, Year, pp. ##-##.
- **Website**: Author. \"Title.\" Website, Date, URL.
- **Database Article**: Author. \"Title.\" Journal, vol. #, no. #, Year, pp. ##-##. Database, URL.

### APA Style (American Psychological Association)

**When to Use APA:**
- **Social Sciences**: Psychology, sociology, anthropology, political science
- **Education**: Educational research and pedagogy studies
- **Business**: Management, marketing, organizational behavior
- **Health Sciences**: Public health, nursing, some medical fields
- **Scientific Writing**: Empirical research in social sciences

**APA In-Text Citations:**

**Author-Date Format:**
- **Basic Citation**: (Author, Year, p. #) - (Smith, 2023, p. 45)
- **Multiple Authors**: (Smith & Jones, 2023, p. 67)
- **No Date**: (Smith, n.d., p. 45)
- **Page Ranges**: (Smith, 2023, pp. 45-47)
- **Paragraph Numbers**: (Smith, 2023, para. 4)

**Signal Phrases:**
- **Author Introduction**: Smith (2023) argued that \"quotation\" (p. 45).
- **Year Emphasis**: In 2023, Smith found that \"quotation\" (p. 45).
- **Paraphrase**: Smith (2023) suggested that the evidence supports this conclusion.
- **Multiple Sources**: Several studies (Smith, 2023; Jones, 2022; Brown, 2021) indicate...

**Reference List Formatting:**
- **Book**: Author, A. A. (Year). Title of book. Publisher.
- **Journal Article**: Author, A. A. (Year). Title of article. Journal Name, Volume(Issue), pages. DOI or URL
- **Website**: Author, A. A. (Year, Month Date). Title of webpage. Website Name. URL
- **Database Article**: Author, A. A. (Year). Title of article. Journal Name, Volume(Issue), pages. Database Name. DOI or URL

### Chicago Style (Chicago Manual of Style)

**When to Use Chicago:**
- **History**: Historical research and analysis
- **Art History**: Studies of visual arts and cultural artifacts
- **Religious Studies**: Theological and religious research
- **Philosophy**: Some philosophical research and writing
- **Publishing**: Book publishing and academic presses

**Chicago Notes-Bibliography System:**

**Footnotes/Endnotes:**
- **First Citation**: Full bibliographic information in note
- **Subsequent Citations**: Shortened form with author and page number
- **Book**: Author, Title (Place: Publisher, Year), page#.
- **Article**: Author, \"Title,\" Journal Volume, no. Issue (Year): page#.

**Bibliography Format:**
- **Book**: Author. Title. Place: Publisher, Year.
- **Article**: Author. \"Title.\" Journal Volume, no. Issue (Year): page range.
- **Website**: Author. \"Title.\" Website. Date. URL.

**Chicago Author-Date System:**
- **Similar to APA**: Used primarily in scientific and social scientific writing
- **In-Text**: (Author Year, page#)
- **Reference List**: Similar format to APA with minor differences

## Source Integration Strategies

### Quotation Guidelines

**When to Quote:**
- **Exact Wording Important**: When precise language is crucial to meaning
- **Authority**: When the source's reputation lends credibility to your argument
- **Style**: When the author's particular way of expressing ideas is significant
- **Evidence**: When the exact words serve as evidence for your claims
- **Memorable Language**: When the phrasing is particularly effective or striking

**Quotation Techniques:**
- **Signal Phrases**: Introduce quotes with context about the author or source
- **Integration**: Make quotes grammatically fit within your sentences
- **Explanation**: Always explain how quotes support your argument
- **Length Appropriateness**: Use block quotes for longer passages (MLA: 4+ lines, APA: 40+ words)
- **Accuracy**: Quote exactly, including any errors (use [sic] to note original errors)

### Paraphrasing and Summary

**Effective Paraphrasing:**
- **Complete Rewording**: Use your own words and sentence structure
- **Maintained Meaning**: Keep the original meaning while changing expression
- **Appropriate Length**: Paraphrases are usually similar in length to original
- **Citation Required**: Always cite paraphrased material
- **Style Integration**: Match your writing style while preserving content

**Summary Techniques:**
- **Condensation**: Reduce longer passages to essential points
- **Main Ideas**: Focus on central arguments rather than supporting details
- **Proportional Representation**: Maintain the relative emphasis of the original
- **Objective Tone**: Present the author's ideas without adding your own interpretation
- **Citation Inclusion**: Always cite summarized material

### Avoiding Plagiarism

**Plagiarism Types:**
- **Direct Plagiarism**: Copying text without quotation marks or citation
- **Mosaic Plagiarism**: Mixing quoted and paraphrased material without proper citation
- **Paraphrase Plagiarism**: Paraphrasing without citation or too closely following original structure
- **Self-Plagiarism**: Reusing your own previously submitted work without permission
- **Accidental Plagiarism**: Improper citation due to misunderstanding or carelessness

**Prevention Strategies:**
- **Careful Note-Taking**: Clearly distinguish between your ideas and source material during research
- **Citation Consistency**: Cite all borrowed ideas, even when paraphrased or summarized
- **Style Guide Adherence**: Follow citation requirements precisely
- **Source Tracking**: Keep detailed records of all sources consulted
- **Time Management**: Allow sufficient time for proper citation and revision

## Research Strategy Development

### Research Planning

**Research Question Development:**
- **Specificity**: Focus on specific, answerable questions rather than broad topics
- **Complexity**: Ensure questions require analysis and argument, not just facts
- **Feasibility**: Consider available sources and time constraints
- **Significance**: Choose questions that matter to your field or audience
- **Originality**: Seek angles that add new perspective to existing discussions

**Source Diversity:**
- **Scholarly Sources**: Peer-reviewed articles and academic books
- **Professional Sources**: Industry reports, white papers, expert interviews
- **Government Sources**: Official data, reports, and policy documents
- **Media Sources**: News articles, documentaries, investigative reports
- **Primary Sources**: Original documents, interviews, surveys, observations

**Research Timeline:**
- **Initial Search**: Broad exploration to understand the topic landscape
- **Focused Research**: Targeted searching based on refined research questions
- **Source Evaluation**: Critical assessment of source quality and relevance
- **Note-Taking**: Systematic recording of information with proper citations
- **Synthesis**: Organizing information to support your argument development

Understanding and applying these research and citation principles ensures that your writing demonstrates academic integrity while effectively incorporating expert knowledge to support your arguments.`
        }
      ],
      quiz: {
        id: "research-documentation-quiz",
        title: "Research and Documentation Quiz",
        description: "Test your understanding of research skills and citation methods",
        timeLimit: 20,
        passingScore: 80,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "What does the 'A' in the CRAAP test for source evaluation represent?",
            options: [
              "Accessibility",
              "Authority", 
              "Accuracy",
              "Both Authority and Accuracy"
            ],
            correctAnswer: 3,
            explanation: "In the CRAAP test, there are two 'A's: Authority (examining the author's credentials and publisher's reputation) and Accuracy (checking for reliable evidence and factual correctness)."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "Which citation style uses author-page format for in-text citations?",
            options: [
              "APA",
              "Chicago",
              "MLA",
              "IEEE"
            ],
            correctAnswer: 2,
            explanation: "MLA style uses author-page format for in-text citations (Author Page#), while APA uses author-date format (Author, Year, p. #)."
          },
          {
            id: "q3",
            type: "multiple-choice",
            question: "When should you use direct quotations rather than paraphrasing?",
            options: [
              "When you need to fill space in your paper",
              "When the exact wording is important to your argument",
              "When you don't understand the source material",
              "When you want to avoid citing sources"
            ],
            correctAnswer: 1,
            explanation: "Direct quotations should be used when the exact wording is crucial to meaning, when the author's authority is important, or when the language is particularly effective or memorable."
          }
        ]
      }
    },
    {
      id: "argumentative-writing",
      title: "Argumentative and Persuasive Writing",
      description: "Developing thesis statements, supporting arguments, and addressing counterarguments",
      order: 3,
      estimatedHours: 5.5,
      content: [
        {
          id: "thesis-development",
          title: "Crafting Strong Thesis Statements and Arguments",
          description: "Building focused, arguable, and supportable thesis statements",
          type: "video",
          duration: 40
        },
        {
          id: "argumentative-essay-lab",
          title: "Argumentative Essay Construction Lab",
          description: "Practice building complete argumentative essays with counterarguments and rebuttals",
          type: "communication-interactive",
          duration: 55,
          interactiveData: {
            exerciseType: "written-communication",
            instructions: "Construct a complete argumentative essay addressing a controversial contemporary issue. Include a clear thesis, multiple supporting arguments, consideration of counterarguments, and proper source integration.",
            scenario: {
              writingContext: "Choose a current debate in education, technology, environment, or social policy. Write a persuasive argumentative essay (750-1000 words) that takes a clear position, supports it with evidence from credible sources, acknowledges and refutes counterarguments, and calls readers to action or changed thinking."
            },
            hints: [
              "Start with a specific, arguable thesis statement",
              "Use topic sentences that clearly support your main claim",
              "Address the strongest counterarguments, not just weak ones",
              "Integrate sources smoothly with signal phrases and proper citations"
            ]
          }
        },
        {
          id: "argument-structure",
          title: "Argument Structure and Logical Organization",
          description: "Classical and Toulmin argument models for persuasive writing",
          type: "text",
          duration: 45,
          textContent: `# Argument Structure and Logical Organization

## Thesis Statement Development

### Characteristics of Strong Thesis Statements

**Arguable Claims:**
- **Debatable Position**: Presents a position that reasonable people could disagree with
- **Specific Stance**: Takes a clear position rather than making obvious or factual statements
- **Significant Issue**: Addresses questions that matter to your audience and context
- **Scope Appropriateness**: Matches the scope of evidence and space available
- **Original Insight**: Offers a fresh perspective or interpretation of the topic

**Thesis Statement Types:**

**Argumentative Thesis:**
- **Position Statement**: Clearly states your stance on a controversial issue
- **Example**: "College athletes should be paid salaries because they generate significant revenue for universities, dedicate full-time hours to their sport, and face financial hardships that interfere with their education."
- **Components**: Clear position + main supporting reasons
- **Function**: Provides roadmap for the entire argument

**Analytical Thesis:**
- **Interpretation Statement**: Presents your analysis or interpretation of a text, event, or phenomenon
- **Example**: "Shakespeare's Hamlet uses the motif of appearance versus reality to explore themes of truth, deception, and the difficulty of knowledge in a corrupt world."
- **Components**: Subject + analytical claim + key supporting elements
- **Function**: Guides analytical exploration and evidence presentation

**Expository Thesis:**
- **Explanatory Statement**: Presents the main points you will explain or inform about
- **Example**: "Effective remote work requires three essential elements: reliable technology infrastructure, clear communication protocols, and disciplined time management strategies."
- **Components**: Topic + main explanatory categories
- **Function**: Organizes informational content logically

### Thesis Revision Strategies

**Specificity Enhancement:**
- **Vague**: "Social media affects teenagers."
- **Specific**: "Excessive social media use among teenagers leads to increased anxiety, decreased face-to-face social skills, and unrealistic self-image expectations."
- **Technique**: Add specific effects, causes, or mechanisms
- **Benefit**: Provides clear direction for research and argumentation

**Scope Adjustment:**
- **Too Broad**: "Education needs reform."
- **Appropriate Scope**: "High school curricula should include mandatory financial literacy courses to prepare students for economic independence."
- **Technique**: Narrow focus to specific aspect, population, or solution
- **Benefit**: Makes argument manageable and thoroughly supportable

**Stake-Raising:**
- **Low Stakes**: "People should recycle more."
- **Higher Stakes**: "Mandatory recycling programs in urban areas are essential to prevent irreversible environmental damage and ensure sustainable resource availability for future generations."
- **Technique**: Emphasize consequences, urgency, or broader implications
- **Benefit**: Motivates audience engagement and action

## Classical Argument Structure

### Traditional Five-Part Structure

**Introduction (Exordium):**
- **Attention Hook**: Engaging opening that draws readers into the topic
- **Context Establishment**: Background information necessary for understanding
- **Credibility Building**: Establishing your authority and good intentions
- **Thesis Presentation**: Clear statement of your position and main arguments
- **Preview**: Brief outline of how you will support your position

**Statement of Facts (Narratio):**
- **Objective Information**: Presenting factual background without argument
- **Shared Understanding**: Establishing common ground with readers
- **Problem Definition**: Clearly explaining the issue requiring resolution
- **Stakeholder Identification**: Explaining who is affected by the issue
- **Historical Context**: Relevant background for understanding current situation

**Division (Partitio):**
- **Argument Outline**: Brief preview of main supporting points
- **Organization Signal**: Helping readers follow your logical structure
- **Emphasis Hierarchy**: Indicating relative importance of different arguments
- **Scope Clarification**: Explaining what you will and won't address
- **Reader Orientation**: Preparing audience for detailed argumentation

**Proof (Confirmatio):**
- **Evidence Presentation**: Detailed support for each main argument
- **Logical Reasoning**: Clear connections between evidence and claims
- **Source Integration**: Effective use of expert testimony, data, and examples
- **Subargument Development**: Breaking complex arguments into manageable parts
- **Coherent Progression**: Logical flow from strongest to weakest or chronological order

**Refutation (Refutatio):**
- **Counterargument Acknowledgment**: Fairly presenting opposing viewpoints
- **Weakness Identification**: Pointing out flaws in opposing arguments
- **Evidence Contradiction**: Providing superior evidence for your position
- **Logic Criticism**: Identifying logical fallacies in opposing arguments
- **Alternative Solutions**: Explaining why your approach is superior

**Conclusion (Peroratio):**
- **Argument Summary**: Reinforcing main points and thesis
- **Emotional Appeal**: Connecting with readers' values and emotions
- **Call to Action**: Specific steps readers should take
- **Broader Significance**: Connecting your argument to larger issues
- **Memorable Closure**: Ending with impact and lasting impression

## Toulmin Argument Model

### Six-Component Structure

**Claim:**
- **Central Assertion**: The main argument you want readers to accept
- **Specificity**: Precise statement of your position
- **Significance**: Important enough to warrant detailed argument
- **Contestability**: Reasonably debatable by informed people
- **Example**: "The federal minimum wage should be raised to $15 per hour."

**Data (Evidence):**
- **Supporting Information**: Facts, statistics, examples, expert testimony
- **Relevance**: Directly related to and supportive of your claim
- **Credibility**: From reliable, authoritative sources
- **Sufficiency**: Adequate quantity and quality to support the claim
- **Example**: "Workers earning minimum wage cannot afford basic living expenses in most U.S. cities, as demonstrated by federal poverty guidelines and cost-of-living analyses."

**Warrant:**
- **Connecting Logic**: The reasoning that links data to claim
- **Assumption Articulation**: Often unstated beliefs that make the argument work
- **Value Statement**: Underlying principles that support the connection
- **Cultural Context**: Shared beliefs or values within the community
- **Example**: "Workers deserve wages that allow them to meet basic needs" (connecting data about unaffordable living costs to claim about wage increases).

**Backing:**
- **Warrant Support**: Evidence that supports the reliability of your warrant
- **Assumption Justification**: Reasons why your underlying assumptions are valid
- **Value Defense**: Explanation of why your values are appropriate and widely shared
- **Context Establishment**: Evidence that your warrant applies in this situation
- **Example**: "Economic research and ethical frameworks support the principle that full-time work should provide living wages, as demonstrated by international labor standards and economic stability studies."

**Qualifier:**
- **Strength Indication**: Words that indicate the strength or certainty of your claim
- **Limitation Acknowledgment**: Recognizing conditions under which your argument might not apply
- **Degree Words**: "Most," "likely," "probably," "in many cases," "generally"
- **Conditional Phrases**: "Unless," "except when," "provided that"
- **Example**: "The federal minimum wage should probably be raised to $15 per hour in most metropolitan areas."

**Rebuttal:**
- **Exception Recognition**: Acknowledging circumstances where your claim might not hold
- **Counterargument Address**: Anticipating and responding to opposing viewpoints
- **Limitation Acceptance**: Being honest about weaknesses in your argument
- **Alternative Consideration**: Acknowledging other valid approaches or solutions
- **Example**: "However, small businesses in rural areas with lower costs of living might need phased implementation or tax incentives to manage the transition."

## Counterargument Strategies

### Effective Counterargument Integration

**Acknowledgment Approaches:**
- **Early Address**: Dealing with major objections in introduction or early paragraphs
- **Point-by-Point**: Addressing counterarguments immediately after presenting each main point
- **Dedicated Section**: Creating separate section for counterargument discussion
- **Conclusion Integration**: Addressing remaining objections in concluding section
- **Throughout Integration**: Weaving counterargument consideration throughout the essay

**Response Strategies:**

**Refutation:**
- **Evidence Contradiction**: Providing stronger evidence that contradicts opposing claims
- **Logic Criticism**: Identifying flaws in reasoning or methodology
- **Source Questioning**: Challenging the credibility or relevance of opposing evidence
- **Context Reframing**: Showing how different contexts change the validity of opposing arguments
- **Alternative Interpretation**: Offering different ways to understand the same evidence

**Concession and Rebuttal:**
- **Partial Agreement**: Acknowledging valid points in opposing arguments
- **Limitation Recognition**: Accepting that opposing views apply in some circumstances
- **Hierarchy Establishment**: Arguing that your concerns outweigh opposing concerns
- **Synthesis Attempt**: Finding common ground while maintaining your position
- **Qualified Response**: Agreeing with parts while disagreeing with overall conclusion

**Redefinition:**
- **Term Clarification**: Showing that disagreement stems from different definitions
- **Scope Adjustment**: Arguing that opposition applies to different scope than your argument
- **Context Specification**: Demonstrating that opposition is valid in different contexts
- **Problem Reframing**: Showing that the real issue is different from what opponents claim
- **Goal Realignment**: Arguing for different priorities or objectives

### Maintaining Credibility

**Fair Representation:**
- **Accuracy**: Presenting opposing views accurately without distortion
- **Strength Selection**: Addressing the strongest rather than weakest opposing arguments
- **Context Preservation**: Maintaining the full context of opposing viewpoints
- **Respectful Tone**: Treating opponents with dignity and intellectual respect
- **Charitable Interpretation**: Giving opposing views the benefit of the doubt

**Balanced Analysis:**
- **Objective Evaluation**: Fairly assessing strengths and weaknesses of all positions
- **Evidence Quality**: Using high-quality sources for both supporting and opposing evidence
- **Logical Consistency**: Applying the same standards of reasoning to all arguments
- **Transparency**: Being open about limitations and uncertainties in your own position
- **Intellectual Honesty**: Acknowledging when opposing arguments have merit

Understanding and applying these argument structures and strategies enables writers to create persuasive, logically sound, and ethically responsible argumentative writing that engages fairly with opposing viewpoints while effectively advocating for their positions.`
        }
      ],
      quiz: {
        id: "argumentative-writing-quiz",
        title: "Argumentative Writing Quiz",
        description: "Test your understanding of argumentative writing principles and structures",
        timeLimit: 15,
        passingScore: 80,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "A strong thesis statement should be:",
            options: [
              "A statement of obvious fact",
              "An arguable position that can be supported with evidence",
              "A question about the topic",
              "A broad generalization about life"
            ],
            correctAnswer: 1,
            explanation: "A strong thesis statement presents an arguable position that reasonable people could disagree with and that can be supported with evidence and reasoning."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "In the Toulmin argument model, what connects the data to the claim?",
            options: [
              "The backing",
              "The qualifier",
              "The warrant",
              "The rebuttal"
            ],
            correctAnswer: 2,
            explanation: "The warrant is the reasoning or assumption that connects the data (evidence) to the claim, often representing shared values or beliefs that make the argument logical."
          },
          {
            id: "q3",
            type: "multiple-choice",
            question: "When addressing counterarguments, you should:",
            options: [
              "Only address the weakest opposing arguments",
              "Ignore counterarguments completely",
              "Address the strongest counterarguments fairly and respectfully",
              "Attack the people who hold opposing views"
            ],
            correctAnswer: 2,
            explanation: "Effective argumentative writing addresses the strongest counterarguments fairly and respectfully, which demonstrates intellectual honesty and strengthens your own credibility."
          }
        ]
      }
    },
    {
      id: "professional-writing",
      title: "Professional and Technical Writing",
      description: "Business communication, reports, proposals, and workplace writing",
      order: 4,
      estimatedHours: 4.5,
      content: [
        {
          id: "workplace-documents",
          title: "Professional Document Types and Formats",
          description: "Memos, reports, proposals, and business correspondence",
          type: "video",
          duration: 35
        },
        {
          id: "professional-writing-workshop",
          title: "Professional Communication Workshop",
          description: "Create workplace documents including proposals, reports, and professional correspondence",
          type: "communication-interactive",
          duration: 50,
          interactiveData: {
            exerciseType: "written-communication",
            instructions: "Develop professional documents for a workplace scenario including a formal proposal, executive summary, and professional email correspondence. Demonstrate understanding of audience needs, professional tone, and document formatting conventions.",
            scenario: {
              writingContext: "You work for a marketing consulting firm and need to propose a new social media strategy for a client company. Create a complete proposal package including: 1) Initial inquiry email to client, 2) Formal proposal document with executive summary, 3) Follow-up email with timeline and next steps."
            },
            hints: [
              "Use professional tone and clear, concise language",
              "Structure documents with clear headings and logical organization",
              "Include specific details like timelines, costs, and expected outcomes",
              "Adapt your communication style for different professional contexts"
            ]
          }
        },
        {
          id: "technical-communication",
          title: "Technical Communication and Clarity",
          description: "Writing clear instructions, procedures, and explanations for technical audiences",
          type: "text",
          duration: 40,
          textContent: `# Technical Communication and Clarity

## Professional Writing Principles

### Audience-Centered Communication

**Professional Audience Analysis:**
- **Hierarchical Awareness**: Understanding organizational structure and decision-making authority
- **Expertise Levels**: Adapting technical detail to audience knowledge and experience
- **Role Responsibilities**: Considering how audience members' jobs affect their information needs
- **Time Constraints**: Recognizing that professional audiences often have limited reading time
- **Decision-Making Context**: Understanding what information audiences need to make decisions

**Stakeholder Consideration:**
- **Primary Audience**: Direct recipients who will act on your communication
- **Secondary Audience**: Others who may read or be affected by your communication
- **Gatekeeper Audience**: People who control access to your primary audience
- **Watchdog Audience**: Regulators, media, or oversight groups who may scrutinize communication
- **Unintended Audience**: People who may gain access to communication unexpectedly

### Professional Tone and Style

**Tone Characteristics:**
- **Professional Courtesy**: Respectful, considerate language that maintains positive relationships
- **Confident Authority**: Demonstrating expertise without arrogance or condescension
- **Appropriate Formality**: Matching level of formality to context and relationship
- **Positive Focus**: Emphasizing solutions and opportunities rather than problems
- **Cultural Sensitivity**: Considering diverse backgrounds and communication preferences

**Clarity Strategies:**
- **Concise Expression**: Using fewer words to express ideas more effectively
- **Active Voice**: Creating energy and clarity through subject-verb-object structure
- **Specific Language**: Choosing precise words rather than vague generalities
- **Logical Organization**: Arranging information in order that serves reader needs
- **Visual Design**: Using formatting, headings, and white space to enhance readability

## Business Document Types

### Email Communication

**Professional Email Structure:**
- **Subject Line**: Specific, action-oriented description of email content
- **Greeting**: Appropriate salutation based on relationship and organizational culture
- **Purpose Statement**: Clear explanation of why you're writing in opening paragraph
- **Body Organization**: Logical presentation of information with clear paragraph breaks
- **Action Items**: Specific requests or next steps clearly identified
- **Professional Closing**: Appropriate sign-off with contact information

**Email Best Practices:**
- **Response Time**: Acknowledging emails promptly, even if full response requires time
- **Thread Management**: Keeping related conversations together while updating subject lines when topics change
- **Attachment Protocol**: Clear file naming, appropriate formats, and size considerations
- **Distribution Lists**: Careful consideration of who needs to receive information
- **Confidentiality**: Appropriate handling of sensitive information and privacy concerns

### Memoranda (Memos)

**Memo Format:**
- **Header Information**: TO, FROM, DATE, SUBJECT fields with complete, accurate information
- **Subject Line**: Specific description of memo content and purpose
- **Opening Statement**: Clear explanation of memo purpose and main message
- **Body Sections**: Organized presentation of information with clear headings
- **Action Items**: Specific requests, deadlines, and responsibilities
- **Conclusion**: Summary of key points and next steps

**Memo Types:**
- **Informational**: Sharing information without requesting action
- **Directive**: Providing instructions or policy changes
- **Request**: Asking for information, resources, or action
- **Recommendation**: Proposing solutions or courses of action
- **Progress Report**: Updating stakeholders on project status

### Business Reports

**Report Structure:**
- **Executive Summary**: Concise overview of main findings and recommendations
- **Introduction**: Context, purpose, scope, and methodology
- **Body Sections**: Organized presentation of findings with supporting evidence
- **Analysis**: Interpretation of data and information
- **Recommendations**: Specific, actionable suggestions based on analysis
- **Conclusion**: Summary of key points and implications
- **Appendices**: Supporting materials that don't belong in main text

**Report Types:**
- **Informational Reports**: Presenting facts and data without analysis or recommendations
- **Analytical Reports**: Including analysis and interpretation of information
- **Recommendation Reports**: Providing specific suggestions for action
- **Progress Reports**: Documenting project status and developments
- **Feasibility Reports**: Evaluating whether proposed actions are practical and beneficial

### Proposals

**Proposal Components:**
- **Problem Statement**: Clear description of issue requiring solution
- **Proposed Solution**: Detailed explanation of recommended approach
- **Methodology**: Specific steps and processes for implementation
- **Timeline**: Realistic schedule with milestones and deliverables
- **Budget**: Detailed cost analysis and resource requirements
- **Qualifications**: Evidence of ability to successfully complete proposed work
- **Benefits**: Clear explanation of value and return on investment

**Proposal Types:**
- **Internal Proposals**: Requesting resources or approval within organization
- **External Proposals**: Responding to requests for proposals (RFPs) from other organizations
- **Grant Proposals**: Seeking funding from foundations or government agencies
- **Sales Proposals**: Proposing products or services to potential clients
- **Research Proposals**: Seeking approval or funding for research projects

## Technical Writing Principles

### Clarity and Precision

**Word Choice:**
- **Concrete Language**: Using specific, tangible terms rather than abstract concepts
- **Technical Accuracy**: Ensuring terminology is correct and used consistently
- **Plain Language**: Avoiding unnecessary jargon while maintaining precision
- **Action-Oriented Verbs**: Using strong verbs that clearly indicate required actions
- **Positive Phrasing**: Expressing requirements in positive rather than negative terms

**Sentence Structure:**
- **Parallel Construction**: Using consistent grammatical structure for similar ideas
- **Appropriate Length**: Balancing completeness with readability
- **Logical Flow**: Organizing information within sentences for maximum clarity
- **Modification Clarity**: Ensuring modifiers clearly relate to intended words
- **Transition Smoothness**: Creating clear connections between ideas

### Organization Strategies

**Document Architecture:**
- **Hierarchical Structure**: Using headings and subheadings to create clear organization
- **Logical Sequence**: Arranging information in order that serves reader needs
- **Chunking**: Breaking complex information into manageable sections
- **Cross-References**: Helping readers navigate between related sections
- **Consistent Formatting**: Using typography and layout to reinforce organization

**Information Presentation:**
- **Lead with Key Information**: Putting most important information first
- **Progressive Disclosure**: Revealing information at appropriate level of detail
- **Comparison Structures**: Using tables, charts, or lists to clarify relationships
- **Visual Hierarchy**: Using design elements to guide reader attention
- **White Space**: Providing visual relief and emphasizing important information

### Instruction Writing

**Procedural Communication:**
- **Task Analysis**: Breaking complex procedures into discrete steps
- **Prerequisite Information**: Ensuring readers have necessary background knowledge
- **Safety Considerations**: Highlighting potential risks and protective measures
- **Troubleshooting**: Anticipating problems and providing solutions
- **Verification Steps**: Including checkpoints to confirm successful completion

**Step-by-Step Format:**
- **Numbered Lists**: Using sequential numbering for procedures that must be followed in order
- **Parallel Structure**: Maintaining consistent grammatical format across steps
- **Action Verbs**: Starting each step with clear action verb
- **Specific Details**: Including necessary specifics without overwhelming readers
- **Visual Support**: Using diagrams, screenshots, or illustrations when helpful

## Professional Communication Ethics

### Honesty and Transparency

**Accurate Representation:**
- **Factual Accuracy**: Ensuring all information presented is correct and current
- **Complete Disclosure**: Including relevant information even when it's unfavorable
- **Source Attribution**: Properly crediting information and ideas from others
- **Limitation Acknowledgment**: Being honest about uncertainties and constraints
- **Error Correction**: Promptly addressing mistakes when they're discovered

**Conflict of Interest:**
- **Interest Disclosure**: Revealing financial or personal interests that might affect judgment
- **Bias Acknowledgment**: Recognizing and communicating potential biases
- **Multiple Perspectives**: Considering and presenting alternative viewpoints when appropriate
- **Professional Boundaries**: Maintaining appropriate relationships and avoiding exploitation
- **Confidentiality Respect**: Protecting sensitive information appropriately

### Inclusive Communication

**Accessibility:**
- **Plain Language**: Using clear, simple language that serves diverse audiences
- **Cultural Sensitivity**: Considering diverse cultural backgrounds and communication preferences
- **Format Accessibility**: Ensuring documents work with assistive technologies
- **Language Inclusion**: Avoiding jargon, idioms, or cultural references that exclude readers
- **Multiple Formats**: Providing information in various formats when possible

**Respectful Language:**
- **Person-First Language**: Emphasizing people rather than characteristics or conditions
- **Gender-Neutral Options**: Using inclusive language that doesn't assume gender identity
- **Cultural Awareness**: Avoiding language that excludes or stereotypes cultural groups
- **Professional Equality**: Treating all colleagues with equal respect regardless of position
- **Constructive Criticism**: Focusing on behaviors and outcomes rather than personal characteristics

Mastering professional and technical communication requires understanding audience needs, organizing information effectively, and communicating with clarity, honesty, and respect for diverse perspectives and backgrounds.`
        }
      ],
      quiz: {
        id: "professional-writing-quiz",
        title: "Professional Writing Quiz",
        description: "Test your understanding of professional and technical writing principles",
        timeLimit: 15,
        passingScore: 80,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "In professional email communication, the subject line should be:",
            options: [
              "Left blank to create curiosity",
              "Specific and action-oriented",
              "Always marked as urgent",
              "The same for all emails in a thread"
            ],
            correctAnswer: 1,
            explanation: "Professional email subject lines should be specific and action-oriented, clearly describing the email content and any required actions to help recipients prioritize and organize their communications."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "When writing instructions, you should:",
            options: [
              "Use passive voice to sound more professional",
              "Include all possible information in each step",
              "Start each step with a clear action verb",
              "Assume readers have expert knowledge"
            ],
            correctAnswer: 2,
            explanation: "Effective instructions start each step with a clear action verb, use parallel structure, and provide specific details without overwhelming the reader."
          },
          {
            id: "q3",
            type: "multiple-choice",
            question: "An executive summary should:",
            options: [
              "Appear at the end of a report",
              "Include only positive findings",
              "Provide a concise overview of main findings and recommendations",
              "Repeat the introduction word-for-word"
            ],
            correctAnswer: 2,
            explanation: "An executive summary appears at the beginning of a report and provides a concise overview of the main findings, analysis, and recommendations for busy executives who may not read the entire document."
          }
        ]
      }
    },
    {
      id: "revision-editing",
      title: "Revision, Editing, and Style Development",
      description: "Developing personal writing style and mastering the revision process",
      order: 5,
      estimatedHours: 4.0,
      content: [
        {
          id: "revision-strategies",
          title: "Global and Local Revision Techniques",
          description: "Systematic approaches to improving content, organization, and style",
          type: "video",
          duration: 35
        },
        {
          id: "style-development-workshop",
          title: "Writing Style and Voice Development",
          description: "Develop your unique writing voice while maintaining clarity and effectiveness",
          type: "communication-interactive",
          duration: 45,
          interactiveData: {
            exerciseType: "written-communication",
            instructions: "Revise and edit a draft essay through multiple revision cycles, focusing on global issues (organization, development, clarity) and local issues (sentence structure, word choice, mechanics). Demonstrate systematic revision and editing skills.",
            scenario: {
              writingContext: "You have written a first draft of a personal narrative essay about a significant learning experience. Use systematic revision strategies to improve the essay's focus, organization, development, style, and correctness through multiple drafts."
            },
            hints: [
              "Start with global issues (thesis, organization, development) before local issues",
              "Read your work aloud to identify awkward phrasing and unclear sentences",
              "Vary sentence structure and length for better flow and rhythm",
              "Edit for conciseness by eliminating unnecessary words and phrases"
            ]
          }
        },
        {
          id: "editing-proofreading",
          title: "Editing and Proofreading for Professional Standards",
          description: "Grammar, mechanics, and style refinement for polished writing",
          type: "text",
          duration: 35,
          textContent: `# Editing and Proofreading for Professional Standards

## Revision Process Framework

### Global Revision (Higher-Order Concerns)

**Content Development:**
- **Thesis Clarity**: Ensuring main argument is clear, specific, and arguable
- **Argument Support**: Evaluating whether evidence adequately supports claims
- **Depth Analysis**: Determining if ideas are developed sufficiently for audience and purpose
- **Relevance Assessment**: Removing or revising content that doesn't serve the main purpose
- **Gap Identification**: Finding places where additional development or evidence is needed

**Organization Evaluation:**
- **Logical Flow**: Checking whether ideas progress logically from introduction to conclusion
- **Paragraph Unity**: Ensuring each paragraph focuses on single main idea
- **Transition Effectiveness**: Evaluating connections between ideas, sentences, and paragraphs
- **Introduction Function**: Assessing whether opening effectively engages readers and previews content
- **Conclusion Impact**: Determining if ending provides satisfying closure and reinforces main points

**Audience Appropriateness:**
- **Tone Consistency**: Maintaining appropriate tone throughout the document
- **Assumption Checking**: Ensuring you don't assume knowledge or attitudes audience may not have
- **Engagement Level**: Evaluating whether content maintains reader interest and attention
- **Accessibility**: Confirming that language and concepts are accessible to intended audience
- **Cultural Sensitivity**: Reviewing for inclusive, respectful language and perspectives

### Local Revision (Lower-Order Concerns)

**Sentence-Level Issues:**
- **Clarity**: Ensuring each sentence expresses its intended meaning clearly
- **Conciseness**: Eliminating unnecessary words and redundant expressions
- **Variety**: Using different sentence structures and lengths for better flow
- **Emphasis**: Positioning important information in prominent sentence positions
- **Coherence**: Creating smooth connections between sentences within paragraphs

**Word Choice Refinement:**
- **Precision**: Choosing words that express exactly what you mean
- **Appropriateness**: Selecting vocabulary suitable for audience and context
- **Freshness**: Avoiding clichés and overused expressions
- **Consistency**: Using terms consistently throughout the document
- **Impact**: Choosing words that create desired emotional and intellectual responses

## Style Development Principles

### Voice and Tone

**Authentic Voice:**
- **Personal Perspective**: Allowing your unique viewpoint and personality to emerge appropriately
- **Confidence**: Writing with authority while acknowledging limitations honestly
- **Consistency**: Maintaining recognizable style characteristics across different pieces
- **Flexibility**: Adapting voice appropriately to different audiences and contexts
- **Growth**: Developing style that evolves with experience and feedback

**Tone Management:**
- **Formal Academic**: Objective, precise, evidence-based, respectful of scholarly conventions
- **Professional Business**: Courteous, efficient, solution-oriented, relationship-aware
- **Personal Narrative**: Reflective, honest, engaging, appropriately intimate
- **Persuasive Advocacy**: Passionate yet reasonable, evidence-based, action-oriented
- **Informational**: Clear, helpful, organized, reader-focused

### Sentence Variety and Flow

**Sentence Types:**
- **Simple Sentences**: Single independent clause for emphasis and clarity
- **Compound Sentences**: Two independent clauses for showing relationships between equal ideas
- **Complex Sentences**: Independent clause with subordinate clause for showing hierarchical relationships
- **Compound-Complex**: Multiple independent clauses with subordination for sophisticated relationships

**Rhythm Creation:**
- **Length Variation**: Mixing short and long sentences for musical quality
- **Stress Patterns**: Positioning important words in stressed positions
- **Parallel Structure**: Using repetition of grammatical patterns for emphasis
- **Periodic vs. Cumulative**: Varying sentence structures for different effects
- **Punctuation Rhythm**: Using punctuation to control pacing and emphasis

### Word Choice Sophistication

**Precision Strategies:**
- **Specific Nouns**: Choosing concrete, specific nouns rather than general ones
- **Strong Verbs**: Using active, precise verbs instead of weak verb + adverb combinations
- **Appropriate Modifiers**: Using adjectives and adverbs that add meaningful information
- **Figurative Language**: Employing metaphors and analogies that clarify rather than confuse
- **Technical Terms**: Using specialized vocabulary appropriately for audience expertise level

**Conciseness Techniques:**
- **Redundancy Elimination**: Removing words that repeat meaning unnecessarily
- **Wordy Phrase Replacement**: Substituting concise expressions for verbose constructions
- **Empty Word Removal**: Eliminating words that add no meaning or emphasis
- **Passive Voice Reduction**: Converting to active voice when it improves clarity and conciseness
- **Nominalization Conversion**: Turning abstract nouns back into concrete verbs when appropriate

## Grammar and Mechanics Mastery

### Common Grammar Issues

**Subject-Verb Agreement:**
- **Compound Subjects**: Ensuring verbs agree with compound subjects joined by "and" or "or"
- **Collective Nouns**: Choosing singular or plural verbs based on collective noun function
- **Indefinite Pronouns**: Matching verbs to singular or plural indefinite pronoun subjects
- **Intervening Phrases**: Ignoring phrases between subject and verb when determining agreement
- **Inverted Sentences**: Identifying true subject in sentences with unusual word order

**Pronoun Usage:**
- **Antecedent Clarity**: Ensuring pronouns clearly refer to specific nouns
- **Case Correctness**: Using subjective, objective, and possessive pronouns appropriately
- **Number Agreement**: Matching singular and plural pronouns to their antecedents
- **Gender Neutrality**: Using inclusive pronouns when gender is unknown or irrelevant
- **Reflexive Pronouns**: Using reflexive pronouns only when subject and object are the same

**Modifier Placement:**
- **Dangling Modifiers**: Ensuring modifying phrases clearly relate to intended words
- **Misplaced Modifiers**: Positioning modifiers next to words they modify
- **Squinting Modifiers**: Avoiding modifiers that could modify more than one element
- **Limiting Modifiers**: Placing words like "only," "just," and "nearly" precisely
- **Participial Phrases**: Ensuring participial phrases modify the correct subjects

### Punctuation Precision

**Comma Usage:**
- **Series Commas**: Using commas (including Oxford commas) in lists consistently
- **Compound Sentences**: Placing commas before coordinating conjunctions in compound sentences
- **Complex Sentences**: Using commas appropriately with subordinate clauses
- **Nonessential Elements**: Setting off nonrestrictive clauses and phrases with commas
- **Introductory Elements**: Using commas after introductory words, phrases, and clauses

**Semicolon and Colon Application:**
- **Independent Clause Connection**: Using semicolons to join closely related independent clauses
- **Complex Series**: Using semicolons in series containing internal commas
- **Introduction Function**: Using colons to introduce lists, quotations, or explanations
- **Emphasis Creation**: Using colons to emphasize what follows
- **Formal Writing**: Applying semicolons and colons appropriately in academic and professional contexts

**Quotation and Citation Punctuation:**
- **Direct Quotation**: Punctuating quoted material according to style guide requirements
- **Integration**: Smoothly incorporating quoted material into your sentences
- **Citation Placement**: Positioning in-text citations correctly relative to punctuation
- **Block Quotation**: Formatting longer quotations appropriately
- **Ellipses and Brackets**: Using ellipses for omissions and brackets for clarifications

## Proofreading Strategies

### Systematic Error Detection

**Multiple Reading Passes:**
- **Content Focus**: First reading for overall meaning and argument effectiveness
- **Organization Check**: Second reading for logical flow and paragraph structure
- **Sentence Level**: Third reading for sentence clarity and variety
- **Word Choice**: Fourth reading for precision and appropriateness
- **Mechanics**: Final reading focused solely on grammar, punctuation, and spelling

**Error Pattern Recognition:**
- **Personal Weakness Identification**: Tracking your most common error types
- **Targeted Checking**: Focusing extra attention on your frequent error patterns
- **Reference Material**: Using grammar handbooks and style guides for uncertain cases
- **Technology Tools**: Using spell-check and grammar-check appropriately while recognizing limitations
- **Fresh Perspective**: Taking breaks between writing and proofreading for objective evaluation

### Professional Standards

**Documentation Accuracy:**
- **Citation Completeness**: Ensuring all borrowed material is properly attributed
- **Format Consistency**: Following chosen style guide requirements precisely
- **Source Reliability**: Double-checking accuracy of bibliographic information
- **Quotation Accuracy**: Verifying that all quotations match original sources exactly
- **Permission Requirements**: Understanding when permission is needed for copyrighted material

**Presentation Quality:**
- **Format Consistency**: Using consistent fonts, spacing, and layout throughout
- **Professional Appearance**: Creating documents that reflect professional standards
- **Accessibility**: Ensuring documents are readable by diverse audiences and assistive technologies
- **File Management**: Using appropriate file names and formats for submission
- **Version Control**: Maintaining organized records of draft progression and revisions

Mastering revision, editing, and style development enables writers to produce polished, professional communication that effectively serves its intended purposes while reflecting the writer's developing expertise and voice.`
        }
      ],
      quiz: {
        id: "revision-editing-quiz",
        title: "Revision and Editing Quiz",
        description: "Test your understanding of revision and editing principles",
        timeLimit: 15,
        passingScore: 80,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "During the revision process, global issues should be addressed:",
            options: [
              "After local issues like grammar and spelling",
              "At the same time as local issues",
              "Before local issues like sentence structure and mechanics",
              "Only if there's time remaining"
            ],
            correctAnswer: 2,
            explanation: "Global issues (content, organization, development) should be addressed before local issues (grammar, mechanics) because major changes to content and structure can make sentence-level editing unnecessary."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "Which sentence revision strategy improves both clarity and conciseness?",
            options: [
              "Adding more adjectives and adverbs",
              "Converting passive voice to active voice when appropriate",
              "Making all sentences the same length",
              "Using only simple sentences"
            ],
            correctAnswer: 1,
            explanation: "Converting passive voice to active voice often makes sentences clearer, more concise, and more engaging by clearly identifying who performs the action."
          },
          {
            id: "q3",
            type: "multiple-choice",
            question: "Effective proofreading involves:",
            options: [
              "Reading through the document once quickly",
              "Only checking for spelling errors",
              "Multiple focused readings for different types of errors",
              "Relying entirely on spell-check software"
            ],
            correctAnswer: 2,
            explanation: "Effective proofreading requires multiple focused readings, each targeting different aspects (content, organization, sentences, words, mechanics) to catch various types of errors."
          }
        ]
      }
    }
  ],

  assignments: [
    {
      id: "portfolio-development",
      title: "Writing Portfolio Development",
      description: "Create a comprehensive portfolio showcasing mastery of different writing genres and skills",
      type: "project", 
      dueWeeks: 12,
      maxScore: 100,
      instructions: `# Writing Portfolio Development Project

## Objective
Develop a comprehensive writing portfolio that demonstrates mastery of key writing skills across multiple genres and contexts.

## Portfolio Components
1. **Argumentative Essay** (750-1000 words): Well-researched position on current issue
2. **Professional Document Package**: Business proposal, memo, and email correspondence
3. **Personal Narrative** (500-750 words): Reflective piece demonstrating style and voice
4. **Research Summary** (300-500 words): Synthesis of sources on chosen topic  
5. **Revision Documentation**: Show revision process for at least two pieces

## Requirements
- Demonstrate mastery of rhetorical strategies and audience awareness
- Show effective research and source integration skills
- Include variety in sentence structure, style, and voice
- Meet professional standards for format, citation, and presentation
- Provide reflection on learning and growth as a writer

## Assessment Criteria
- Quality of writing and adherence to genre conventions
- Effectiveness of rhetorical strategies and audience adaptation
- Research skills and proper documentation
- Evidence of revision and editing skills
- Portfolio organization and professional presentation`,
      submissionFormat: "pdf",
      resources: [
        "Genre-specific writing guides and examples",
        "Citation style handbooks and online resources", 
        "Professional portfolio templates and formats"
      ]
    }
  ],

  finalExam: {
    id: "english-composition-final",
    title: "English Composition Final Exam",
    description: "Comprehensive examination of writing skills and rhetorical knowledge",
    timeLimit: 120,
    passingScore: 85,
    attempts: 2,
    questions: [
      {
        id: "final-q1",
        type: "multiple-choice",
        question: "Which rhetorical appeal would be most appropriate for the opening of an academic research paper?",
        options: [
          "Pathos - emotional story about the topic",
          "Ethos - establishing credibility through expertise and research",
          "Logos - presenting statistical data immediately",
          "All appeals should be used equally"
        ],
        correctAnswer: 1,
        explanation: "Academic writing typically begins by establishing ethos (credibility) through demonstration of research, expertise, and understanding of scholarly conversation before presenting logical arguments and evidence."
      },
      {
        id: "final-q2",
        type: "multiple-choice",
        question: "When integrating sources into your writing, you should:",
        options: [
          "Let quotations speak for themselves without explanation",
          "Always introduce sources with signal phrases and explain their relevance",
          "Use as many direct quotations as possible",
          "Avoid mentioning the source's credentials"
        ],
        correctAnswer: 1,
        explanation: "Effective source integration requires introducing sources with signal phrases that provide context and always explaining how the source supports your argument or adds to the discussion."
      }
    ]
  },

  quizzes: [
    {
      id: "comprehensive-composition-quiz",
      title: "Comprehensive English Composition Quiz",
      description: "Advanced quiz covering all aspects of academic and professional writing",
      timeLimit: 30,
      passingScore: 85,
      attempts: 2,
      questions: [
        {
          id: "comp-q1",
          type: "multiple-choice",
          question: "Which revision strategy should be applied first when improving a draft?",
          options: [
            "Correcting spelling and punctuation errors",
            "Improving sentence variety and word choice",
            "Evaluating thesis clarity and argument development",
            "Adjusting tone and style"
          ],
          correctAnswer: 2,
          explanation: "Global revision concerns like thesis clarity and argument development should be addressed before local concerns like sentence structure and mechanics, since major changes might make detailed editing unnecessary."
        },
        {
          id: "comp-q2",
          type: "multiple-choice",
          question: "In professional writing, conciseness is achieved by:",
          options: [
            "Using only short sentences",
            "Eliminating all descriptive language",
            "Removing unnecessary words while maintaining clarity",
            "Avoiding complex ideas"
          ],
          correctAnswer: 2,
          explanation: "Professional conciseness involves eliminating unnecessary words, redundancy, and wordy constructions while maintaining clarity and completeness of communication."
        },
        {
          id: "comp-q3",
          type: "multiple-choice",
          question: "The CRAAP test for source evaluation includes all of the following except:",
          options: [
            "Currency - how recent the information is",
            "Relevance - how the source relates to your topic",
            "Creativity - how original the source content is",
            "Authority - the credibility of the author and publisher"
          ],
          correctAnswer: 2,
          explanation: "The CRAAP test evaluates Currency, Relevance, Authority, Accuracy, and Purpose. Creativity is not one of the criteria for academic source evaluation."
        }
      ]
    }
  ]
};